import React, { useCallback } from 'react';
import { Box } from '@mui/material';
import { colors, Typography } from '@sweep-io/sweep-design';
import { useSelector } from 'react-redux';
import { useIssuesPanelWidth } from '../../hooks/useIssuesPanelWidth';
import { selectIsIssuesPanelOpen } from '../aiAgentCenter/agentsHomeReducer';
import useAgentsHome from '../aiAgentCenter/useAgentsHome';
import useAgentsChats from '../aiAgentCenter/useAgentsChats';
import { selectDefaultCreationCrmOrgId } from '../../reducers/userInfoReducer';
import useObjectTypesWithFetch from '../../hooks/useObjectTypesWithFetch';
import IssuesTableWithFilters from './IssuesTableWithFilters';
import { OnUpdateIssueStatus } from '../aiAgentCenter/aiAgentsConsts';
import { IssueStatus } from '@server/issues';
import useAiAgents from '../aiAgentCenter/useAiAgents';
import { useSweepNotifications } from '../notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../../reducers/notificationsReducer';
import { ResizableInlinePanel } from '../common/panel/ResizableInlinePanel';

export const IssuesPanel: React.FC = () => {
  const isIssuesPanelOpen = useSelector(selectIsIssuesPanelOpen);
  const { activeAgent } = useAgentsChats();
  const { setReplyText, closeIssuesPanel } = useAgentsHome();
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const { objectTypesByName } = useObjectTypesWithFetch({ crmOrgId: crmOrgId ?? '' });
  const { updateIssueStatus } = useAiAgents();
  const { addNotification } = useSweepNotifications();

  const { panelWidth, updatePanelWidth, minWidth, maxWidth } = useIssuesPanelWidth();

  const handleClose = useCallback(() => {
    closeIssuesPanel();
  }, [closeIssuesPanel]);

  const handleUpdateIssueStatus: OnUpdateIssueStatus = useCallback(
    async ({ resourceName, issueId, status }) => {
      if (activeAgent) {
        const updatedSuccessfully = await updateIssueStatus({
          agentId: activeAgent.id,
          issueId,
          newStatus: status as IssueStatus,
        });
        if (updatedSuccessfully) {
          addNotification({
            message: `${resourceName} is marked as ${status}`,
            variant: SweepNotificationVariant.Success,
          });
        }
      }
    },
    [activeAgent, addNotification, updateIssueStatus],
  );

  const handleTextSelection = useCallback(
    (text: string | null) => {
      setReplyText(text);
    },
    [setReplyText],
  );

  if (!isIssuesPanelOpen || !activeAgent) {
    return null;
  }

  const issues = activeAgent.issues ?? [];
  const objectType =
    'objectType' in activeAgent.contextComponents
      ? activeAgent.contextComponents.objectType
      : undefined;
  const objectLabel = objectType ? (objectTypesByName[objectType]?.label ?? objectType) : '';
  const issuesCount = issues.length;

  const header = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 0 0 16px',
        gap: 1.5,
      }}
    >
      <Typography variant="h3">Monitoring</Typography>
      <Typography variant="body" color={colors.grey[700]}>
        {issuesCount} Issues Found{objectLabel ? ` in the ${objectLabel} Object` : ''}
      </Typography>
    </Box>
  );

  return (
    <ResizableInlinePanel
      panelWidth={panelWidth}
      onResize={updatePanelWidth}
      minWidth={minWidth}
      maxWidth={maxWidth}
      onClose={handleClose}
      header={header}
      showHeaderBorder={false}
    >
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: '16px',
        }}
      >
        <IssuesTableWithFilters
          onUpdateIssueStatus={handleUpdateIssueStatus}
          issues={issues}
          activeScan={activeAgent.activeScan}
          onTextSelection={handleTextSelection}
        />
      </Box>
    </ResizableInlinePanel>
  );
};

export default IssuesPanel;
